// @ts-strict

import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

interface UploadedImageData {
  data: File;
  fileName: string;
}

export interface UploadedImage {
  file?: UploadedImageData;
  initialData?: boolean;
  thumbnail: string;
}

@Component({
  selector: 'app-img-upload-input',
  templateUrl: './img-upload-input.component.html',
  styleUrls: ['./img-upload-input.component.scss']
})
export class ImgUploadInputComponent implements OnInit {
  @Input() required = false;
  @Input() submitted = false;
  @Input() numberOfRequiredImg?: number;
  @Input() maxSize = 5_000_000;
  @Input() initialData: any = [];
  @Output() imgFiles = new EventEmitter<UploadedImage[]>();
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  public fileTypeError = false;
  public fileSizeError = false;
  public uploadedImg: UploadedImage[] = [];

  public files: File[] = [];

  constructor(private readonly http: HttpClient) {}

  ngOnInit(): void {
    if (this.initialData.length) {
      this.makeRequests(this.initialData);
    }
  }

  public onRemoveImg(image: UploadedImage): void {
    if (image?.initialData) {
      this.uploadedImg = this.uploadedImg.filter(el => el.thumbnail !== image.thumbnail);
    } else {
      this.uploadedImg = this.uploadedImg.filter(el => el.file !== image.file);
    }
    this.imgFiles.emit(this.uploadedImg);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.addFiles(event.dataTransfer?.files || null);
  }

  public fileBrowseHandler(event: Event): void {
    const target = event.target;

    if (target instanceof HTMLInputElement) {
      this.addFiles(target.files);
    }
  }

  private addFile(file: File): void {
    if (['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
      this.fileTypeError = false;
    } else {
      this.fileTypeError = true;
    }
    this.fileSizeError = file.size > this.maxSize;
    if (!this.fileSizeError && !this.fileTypeError) {
      const fileToUpload: UploadedImageData = {
        data: file,
        fileName: file.name
      };

      const reader = new FileReader();

      reader.onload = () => {
        this.uploadedImg.push({ file: fileToUpload, thumbnail: reader.result as string });
        this.imgFiles.emit(this.uploadedImg);
      };

      reader.readAsDataURL(file);
    }
  }

  private addFiles(files: FileList | null): void {
    if (!files?.length) return;

    Array.from(files).forEach(file => this.addFile(file));

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private makeRequests = (urls: string[]) => {
    const requests: Observable<File>[] = urls.map(url =>
      this.http.get(url, { responseType: 'blob' }).pipe(
        map(res => {
          const myFile = new File([res], 'image.png', {
            type: 'image/png'
          });

          return myFile;
        })
      )
    );

    forkJoin(requests)
      .pipe(tap(files => files.forEach(file => this.addFile(file))))
      .subscribe();
  };
}
